'use client';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'next/navigation';
import { ProductGrid as Shared } from '@launchstore/shared-puck';
import { SORT_KEY_TO_MEDUSA } from '@launchstore/shared-puck';
import type { SortKey } from '@launchstore/shared-puck';
import { usePuckContext } from '@/components/puck/PuckContextProvider';
import { useCategory } from '@lib/hooks/useCategory';
import { listProducts } from '@lib/data/products';
import { convertToLocale } from '@lib/util/money';
import TemplateProductCard from '@modules/products/components/product-preview/template-product-card';

const SharedRender = (Shared as any).render;
const SharedGrid: React.FC<any> = (props) => <SharedRender {...props} />;

const DEFAULT_PER_PAGE = 12;

function ProductGridImpl(props: any) {
  const { context } = usePuckContext() as any;
  const collection = context?.collection;
  const countryCode = context?.countryCode;
  const productCardTemplate = context?.productCardTemplate;
  const searchParams = useSearchParams();

  // Also check if we're on a category page (via useCategory hook)
  let category: any = null;
  try {
    const catResult = useCategory() as any;
    category = catResult?.category;
  } catch {
    // useCategory throws if not in a Puck category context — that's fine
  }

  const sortKey = (searchParams.get('sortBy') || 'created_desc') as SortKey;
  const page = parseInt(searchParams.get('page') || '1', 10);
  const perPage = parseInt(searchParams.get('perPage') || String(DEFAULT_PER_PAGE), 10);

  const [products, setProducts] = useState<any[] | null>(null);
  const [region, setRegion] = useState<any>(null);

  // Determine the data source: category page, collection page, or store page (all products)
  const categoryId = category?.id;
  const collectionId = collection?.id;

  useEffect(() => {
    if (!countryCode) { setProducts(null); return; }
    (async () => {
      try {
        const queryParams: any = {
          limit: perPage,
          offset: (page - 1) * perPage,
          fields: '*variants.calculated_price,+variants.inventory_quantity,+variants.manage_inventory,+variants.allow_backorder',
          order: SORT_KEY_TO_MEDUSA[sortKey] || SORT_KEY_TO_MEDUSA.created_desc,
        };
        if (categoryId) {
          queryParams.category_id = categoryId;
        } else if (collectionId) {
          queryParams.collection_id = [collectionId];
        }
        // If neither → fetch ALL products (store page / search)
        const result = await listProducts({ queryParams, countryCode });
        setProducts(result.response.products || []);
      } catch { setProducts([]); }
    })();
  }, [categoryId, collectionId, countryCode, sortKey, page, perPage]);

  useEffect(() => {
    import('@lib/data/regions').then(async ({ listRegions }) => {
      try { const r = await listRegions(); if (r?.length) setRegion(r[0]); } catch {}
    }).catch(() => {});
  }, []);

  const canUseTemplateCard = !!(productCardTemplate?.puckData && region);

  const renderProduct = useCallback((p: any) => {
    if (canUseTemplateCard) {
      return <TemplateProductCard product={p} region={region} productCardTemplate={productCardTemplate} countryCode={countryCode} />;
    }
    return undefined;
  }, [canUseTemplateCard, productCardTemplate, region, countryCode]);

  // Conditional returns (after all hooks)
  if (context?.sampleProducts) {
    return <SharedGrid {...props} products={context.sampleProducts} />;
  }
  if (products === null) return <></>;

  const currency = region?.currency_code || collection?.currency_code || 'usd';
  const displayProducts = canUseTemplateCard ? products : products.map((p: any) => {
    const cheapest = p.variants?.reduce((min: any, v: any) => {
      const price = v.calculated_price?.calculated_amount;
      if (price == null) return min;
      return min == null || price < min ? price : min;
    }, null as number | null);
    return {
      id: p.id, title: p.title, handle: p.handle, thumbnail: p.thumbnail,
      price: cheapest != null ? convertToLocale({ amount: cheapest, currency_code: currency }) : '',
    };
  });

  return <SharedGrid {...props} products={displayProducts} renderProduct={renderProduct} />;
}

export const ProductGrid: any = {
  ...Shared,
  render: (props: any) => <ProductGridImpl {...props} />,
};
export default ProductGrid;
